import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { getUsuarios, createUsuario, updateUsuario, resetPassword, downloadUsuariosTemplate, deleteUsuario } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { ScrollArea } from '../components/ui/scroll-area';
import { Switch } from '../components/ui/switch';
import { 
  Plus, PencilSimple, Key, Download, Users, ShieldCheck, Trash
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import ConfirmDeleteDialog from '../components/ConfirmDeleteDialog';

const ROLES = [
  { value: 'administrador', label: 'Administrador' },
  { value: 'direccion', label: 'Dirección' },
  { value: 'prefectura', label: 'Prefectura' },
  { value: 'docente', label: 'Docente' },
  { value: 'administrativo', label: 'Administrativo' }
];

const MODULOS_DISPONIBLES = [
  { value: 'dashboard', label: 'Dashboard' },
  { value: 'inscripciones', label: 'Inscripciones' },
  { value: 'alumnos', label: 'Alumnos' },
  { value: 'asistencia', label: 'Asistencia' },
  { value: 'expediente', label: 'Expediente' },
  { value: 'bitacora', label: 'Bitácora' },
  { value: 'patre', label: 'PATRE' },
  { value: 'alertas', label: 'Alertas' },
  { value: 'reportes', label: 'Reportes' },
  { value: 'credencialización', label: 'Credencialización' },
];

const UsuariosPage = () => {
  const { user, hasPermission } = useAuth();
  const canDelete = hasPermission(['administrador', 'direccion']);
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [selectedUsuario, setSelectedUsuario] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [formData, setFormData] = useState({
    username: '',
    password: '',
    nombre_completo: '',
    rol: '',
    turno: '',
    grados_asignados: [],
    grupos_asignados: [],
    materias: [],
    modulos_permitidos: []
  });

  const fetchUsuarios = async () => {
    setLoading(true);
    try {
      const response = await getUsuarios();
      setUsuarios(response.data);
    } catch (error) {
      toast.error('Error al cargar usuarios');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsuarios();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editMode && selectedUsuario) {
        await updateUsuario(selectedUsuario.id, {
          nombre_completo: formData.nombre_completo,
          rol: formData.rol,
          turno: formData.turno && formData.turno !== 'all' ? formData.turno : null,
          grados_asignados: formData.grados_asignados,
          grupos_asignados: formData.grupos_asignados,
          materias: formData.materias,
          modulos_permitidos: formData.modulos_permitidos,
          activo: formData.activo
        });
        toast.success('Usuario actualizado');
      } else {
        const dataToSend = {
          username: formData.username,
          password: formData.password,
          nombre_completo: formData.nombre_completo,
          rol: formData.rol,
          turno: formData.turno && formData.turno !== 'all' ? formData.turno : null,
          grados_asignados: formData.grados_asignados,
          grupos_asignados: formData.grupos_asignados,
          materias: formData.materias,
          modulos_permitidos: formData.modulos_permitidos
        };
        await createUsuario(dataToSend);
        toast.success('Usuario creado');
      }
      setShowModal(false);
      resetForm();
      fetchUsuarios();
    } catch (error) {
      const errorMsg = error.response?.data?.detail;
      toast.error(typeof errorMsg === 'string' ? errorMsg : 'Error al guardar');
    }
  };

  const handleResetPassword = async () => {
    if (!selectedUsuario || !newPassword) return;
    try {
      await resetPassword(selectedUsuario.id, newPassword);
      toast.success('Contraseña restablecida');
      setShowResetModal(false);
      setNewPassword('');
    } catch (error) {
      toast.error('Error al restablecer contraseña');
    }
  };

  const openEditModal = (usuario) => {
    setSelectedUsuario(usuario);
    setFormData({
      username: usuario.username,
      nombre_completo: usuario.nombre_completo,
      rol: usuario.rol,
      turno: usuario.turno || '',
      grados_asignados: usuario.grados_asignados || [],
      grupos_asignados: usuario.grupos_asignados || [],
      materias: usuario.materias || [],
      modulos_permitidos: usuario.modulos_permitidos || [],
      activo: usuario.activo
    });
    setEditMode(true);
    setShowModal(true);
  };

  const openResetModal = (usuario) => {
    setSelectedUsuario(usuario);
    setNewPassword('');
    setShowResetModal(true);
  };

  const resetForm = () => {
    setFormData({
      username: '',
      password: '',
      nombre_completo: '',
      rol: '',
      turno: '',
      grados_asignados: [],
      grupos_asignados: [],
      materias: [],
      modulos_permitidos: []
    });
    setSelectedUsuario(null);
    setEditMode(false);
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await downloadUsuariosTemplate();
      const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'template_usuarios.xlsx';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Error al descargar plantilla');
    }
  };

  const getRolColor = (rol) => {
    const colors = {
      administrador: 'bg-purple-100 text-purple-800',
      direccion: 'bg-blue-100 text-blue-800',
      prefectura: 'bg-green-100 text-green-800',
      docente: 'bg-amber-100 text-amber-800'
    };
    return colors[rol] || 'bg-gray-100 text-gray-800';
  };

  const toggleArrayItem = (field, value) => {
    setFormData(prev => {
      const arr = prev[field];
      const newArr = arr.includes(value)
        ? arr.filter(v => v !== value)
        : [...arr, value];
      return { ...prev, [field]: newArr };
    });
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="usuarios-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold">Usuarios</h1>
          <p className="text-muted-foreground">Administración de usuarios del sistema</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button 
            variant="outline" 
            size="sm"
            onClick={handleDownloadTemplate}
          >
            <Download size={16} className="mr-1" />
            Plantilla
          </Button>
          
          {hasPermission(['administrador']) && (
            <Button 
              size="sm"
              onClick={() => { resetForm(); setShowModal(true); }}
              data-testid="add-usuario"
            >
              <Plus size={16} className="mr-1" />
              Nuevo Usuario
            </Button>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <Users size={24} className="text-primary" weight="duotone" />
            <div>
              <p className="text-2xl font-bold">{usuarios.length}</p>
              <p className="text-sm text-muted-foreground">Total</p>
            </div>
          </CardContent>
        </Card>
        {ROLES.map(rol => (
          <Card key={rol.value}>
            <CardContent className="p-4">
              <p className="text-2xl font-bold">
                {usuarios.filter(u => u.rol === rol.value).length}
              </p>
              <p className="text-sm text-muted-foreground">{rol.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Table */}
      <Card>
        <CardHeader className="py-4">
          <CardTitle className="text-base font-medium flex items-center gap-2">
            <ShieldCheck size={20} />
            Lista de Usuarios
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <ScrollArea className="w-full">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead>Usuario</TableHead>
                  <TableHead>Nombre Completo</TableHead>
                  <TableHead className="w-[120px]">Rol</TableHead>
                  <TableHead className="w-[100px]">Turno</TableHead>
                  <TableHead>Asignaciones</TableHead>
                  <TableHead className="w-[80px]">Estado</TableHead>
                  <TableHead className="w-[120px] text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8">
                      <div className="spinner mx-auto" />
                    </TableCell>
                  </TableRow>
                ) : usuarios.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                      No hay usuarios registrados
                    </TableCell>
                  </TableRow>
                ) : (
                  usuarios.map((usuario) => (
                    <TableRow key={usuario.id} className="hover:bg-muted/30">
                      <TableCell className="font-mono text-sm">{usuario.username}</TableCell>
                      <TableCell className="font-medium">{usuario.nombre_completo}</TableCell>
                      <TableCell>
                        <Badge className={`text-xs ${getRolColor(usuario.rol)}`}>
                          {ROLES.find(r => r.value === usuario.rol)?.label}
                        </Badge>
                      </TableCell>
                      <TableCell className="capitalize">{usuario.turno || '-'}</TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {usuario.grados_asignados?.map(g => (
                            <Badge key={g} variant="outline" className="text-xs">{g}°</Badge>
                          ))}
                          {usuario.grupos_asignados?.map(g => (
                            <Badge key={g} variant="outline" className="text-xs">{g}</Badge>
                          ))}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant={usuario.activo ? 'default' : 'secondary'} className="text-xs">
                          {usuario.activo ? 'Activo' : 'Inactivo'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        {hasPermission(['administrador']) && (
                          <div className="flex justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => openEditModal(usuario)}
                              data-testid={`edit-${usuario.username}`}
                            >
                              <PencilSimple size={16} />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => openResetModal(usuario)}
                              data-testid={`reset-${usuario.username}`}
                            >
                              <Key size={16} />
                            </Button>
                            {canDelete && usuario.username !== 'admin' && usuario.id !== user?.id && (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-destructive"
                                onClick={() => setDeleteTarget(usuario)}
                                title="Eliminar usuario (irreversible)"
                                data-testid={`delete-${usuario.username}`}
                              >
                                <Trash size={16} />
                              </Button>
                            )}
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </ScrollArea>
        </CardContent>
      </Card>

      {/* Create/Edit Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {editMode ? 'Editar Usuario' : 'Nuevo Usuario'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Usuario *</Label>
                <Input
                  value={formData.username}
                  onChange={(e) => setFormData(p => ({ ...p, username: e.target.value }))}
                  required
                  disabled={editMode}
                  data-testid="form-username"
                />
              </div>
              {!editMode && (
                <div>
                  <Label>Contraseña *</Label>
                  <Input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData(p => ({ ...p, password: e.target.value }))}
                    required
                    data-testid="form-password"
                  />
                </div>
              )}
            </div>
            <div>
              <Label>Nombre Completo *</Label>
              <Input
                value={formData.nombre_completo}
                onChange={(e) => setFormData(p => ({ ...p, nombre_completo: e.target.value }))}
                required
                data-testid="form-nombre"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Rol *</Label>
                <Select 
                  value={formData.rol} 
                  onValueChange={(v) => setFormData(p => ({ ...p, rol: v }))}
                >
                  <SelectTrigger data-testid="form-rol">
                    <SelectValue placeholder="Seleccionar" />
                  </SelectTrigger>
                  <SelectContent>
                    {ROLES.map(r => (
                      <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Turno</Label>
                <Select 
                  value={formData.turno} 
                  onValueChange={(v) => setFormData(p => ({ ...p, turno: v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Todos" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    <SelectItem value="matutino">Matutino</SelectItem>
                    <SelectItem value="vespertino">Vespertino</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {(formData.rol === 'prefectura' || formData.rol === 'docente') && (
              <>
                <div>
                  <Label>Grados Asignados</Label>
                  <div className="flex gap-2 mt-2">
                    {['1', '2', '3'].map(g => (
                      <Button
                        key={g}
                        type="button"
                        variant={formData.grados_asignados.includes(g) ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => toggleArrayItem('grados_asignados', g)}
                      >
                        {g}°
                      </Button>
                    ))}
                  </div>
                </div>
                <div>
                  <Label>Grupos Asignados</Label>
                  <div className="flex gap-2 mt-2">
                    {['A', 'B', 'C', 'D'].map(g => (
                      <Button
                        key={g}
                        type="button"
                        variant={formData.grupos_asignados.includes(g) ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => toggleArrayItem('grupos_asignados', g)}
                      >
                        {g}
                      </Button>
                    ))}
                  </div>
                </div>
              </>
            )}
            
            {formData.rol === 'docente' && (
              <div>
                <Label>Materias</Label>
                <Input
                  value={formData.materias.join(', ')}
                  onChange={(e) => setFormData(p => ({ ...p, materias: e.target.value.split(',').map(m => m.trim()).filter(Boolean) }))}
                  placeholder="Matemáticas, Español, Ciencias..."
                />
                <p className="text-xs text-muted-foreground mt-1">Separar con comas</p>
              </div>
            )}

            {formData.rol === 'administrativo' && (
              <div>
                <Label>Módulos permitidos</Label>
                <p className="text-xs text-muted-foreground mt-1 mb-2">
                  Selecciona los módulos a los que tendrá acceso este usuario administrativo.
                </p>
                <div className="grid grid-cols-2 gap-2 p-3 rounded-md bg-muted/40 border">
                  {MODULOS_DISPONIBLES.map((m) => {
                    const checked = formData.modulos_permitidos.includes(m.value);
                    return (
                      <label
                        key={m.value}
                        className="flex items-center gap-2 cursor-pointer text-sm select-none"
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={(e) => {
                            setFormData((p) => {
                              const set = new Set(p.modulos_permitidos);
                              if (e.target.checked) set.add(m.value);
                              else set.delete(m.value);
                              return { ...p, modulos_permitidos: Array.from(set) };
                            });
                          }}
                          data-testid={`modulo-${m.value}`}
                          className="h-4 w-4 accent-primary"
                        />
                        <span>{m.label}</span>
                      </label>
                    );
                  })}
                </div>
                <p className="text-xs text-amber-600 mt-2">
                  Módulos de seguridad (Usuarios, Auditoría, Configuración) solo están disponibles para Administrador y Dirección.
                </p>
              </div>
            )}
            
            {editMode && (
              <div className="flex items-center justify-between">
                <Label>Usuario Activo</Label>
                <Switch
                  checked={formData.activo}
                  onCheckedChange={(v) => setFormData(p => ({ ...p, activo: v }))}
                />
              </div>
            )}
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowModal(false)}>
                Cancelar
              </Button>
              <Button type="submit" data-testid="submit-usuario">
                {editMode ? 'Guardar Cambios' : 'Crear Usuario'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Reset Password Modal */}
      <Dialog open={showResetModal} onOpenChange={setShowResetModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading">Restablecer Contraseña</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Restablecer contraseña para: <strong>{selectedUsuario?.nombre_completo}</strong>
            </p>
            <div>
              <Label>Nueva Contraseña</Label>
              <Input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Nueva contraseña"
                data-testid="new-password-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowResetModal(false)}>
              Cancelar
            </Button>
            <Button onClick={handleResetPassword} disabled={!newPassword}>
              Restablecer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDeleteDialog
        open={!!deleteTarget}
        onOpenChange={(v) => { if (!v) setDeleteTarget(null); }}
        title="Eliminar usuario"
        description="El usuario será dado de baja y no podrá volver a iniciar sesión. Esta acción es irreversible."
        itemLabel={deleteTarget ? `${deleteTarget.nombre_completo} (${deleteTarget.username})` : ''}
        onConfirm={async (motivo) => {
          setDeleting(true);
          try {
            await deleteUsuario(deleteTarget.id, motivo);
            toast.success(`Usuario ${deleteTarget.username} eliminado`);
            setDeleteTarget(null);
            fetchUsuarios();
          } catch (error) {
            toast.error(error.response?.data?.detail || 'No se pudo eliminar el usuario');
          } finally {
            setDeleting(false);
          }
        }}
        loading={deleting}
      />
    </div>
  );
};

export default UsuariosPage;
